import { Component, computed, inject, OnDestroy, OnInit, signal, TemplateRef, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SwalComponent } from '@sweetalert2/ngx-sweetalert2';
import { finalize } from 'rxjs/operators';
import { hasStatus, tryGetErrorMessage } from '../../../../../error.utils';
import { AssignmentService } from '../../../../../service/assignment.service';
import { LossPreventionService } from '../../../../../service/loss-prevention.service';
import { NotifyService } from '../../../../../service/notify.service';
import {
  AssignmentDetails,
  IncidentFormContext,
  IncidentFormGroup,
  ReviewOutcome,
  ReviewStatus,
  ReviewSubjectType,
  SubmitReviewRequest,
} from '../../../../../types';
import { ReviewSubmissionFailedModalComponent } from '../../review-submission-failed-modal/review-submission-failed-modal.component';
import { ReviewFormService } from '../../review-submit-form/review-form.service';
import { parseReviewStatus, parseReviewSubjectType, reviewOutcomeToFormValues } from '../../review-utils';
import { AssignmentDetailsStore } from '../../investigation-assignment/assignment-details.store';

@Component({
  selector: 'app-inspect-assignment',
  templateUrl: './inspect-assignment.component.html',
  styleUrl: './inspect-assignment.component.scss',
  standalone: false,
  providers: [AssignmentDetailsStore],
})
export class InspectAssignmentComponent implements OnDestroy, OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private assignmentService = inject(AssignmentService);
  private lpService = inject(LossPreventionService);
  private notifyService = inject(NotifyService);
  public reviewFormService = inject(ReviewFormService);
  private assignmentDetailsStore = inject(AssignmentDetailsStore);

  public readonly ReviewStatus = ReviewStatus;

  public form = signal<IncidentFormGroup | undefined>(undefined);
  public incidentFormRef = signal<TemplateRef<unknown> | undefined>(undefined);

  public assignmentIdToInspect = signal<string | undefined>(undefined);
  public assignmentBeingInspected = signal<AssignmentDetails | undefined>(undefined);
  public assignmentType = signal<ReviewSubjectType | undefined>(undefined);
  public assignmentStatus = signal<ReviewStatus | undefined>(undefined);
  public readonly ReviewSubjectType = ReviewSubjectType;
  public passwordAction = signal<'clear' | 'refresh'>('clear');
  public incidentDate = signal<Date | undefined>(undefined);

  public isLoading = signal<boolean>(false);
  public isSubmitting = signal<boolean>(false);
  public isRecovering = signal<boolean>(false);
  public loadErrorMessage = signal<string | undefined>(undefined);
  public submitErrorMessage = signal<string | undefined>(undefined);
  public reviewOutcome = signal<ReviewOutcome | undefined>(undefined);

  private submittedSwal = viewChild<SwalComponent>('submittedSwal');
  private failedModal = viewChild<ReviewSubmissionFailedModalComponent>('failedModal');

  public readonly uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  public isAssignmentIdValid = computed(() => !!this.assignmentIdToInspect() && this.uuidRegex.test(this.assignmentIdToInspect()));
  public isForceReviewEnabled = computed(() => this.assignmentStatus() === 'completed' || this.assignmentStatus() === 'error');

  public isRecoverEnabled = computed(() => {
    if (this.assignmentStatus() !== ReviewStatus.Error) return false;
    const type = this.assignmentType();
    if (type !== ReviewSubjectType.Cancellation && type !== ReviewSubjectType.Walkout) return false;
    const eventDate = this.incidentDate();
    if (!eventDate) return false;
    const daysSinceEvent = (Date.now() - eventDate.getTime()) / (1000 * 60 * 60 * 24);
    return daysSinceEvent < 6;
  });

  public recoverDisabledReason = computed(() => {
    const type = this.assignmentType();
    if (type !== ReviewSubjectType.Cancellation && type !== ReviewSubjectType.Walkout) {
      return 'Only Cancellation and Walkout reviews can be recovered';
    }
    const eventDate = this.incidentDate();
    if (!eventDate) return 'Event date not available';
    const daysSinceEvent = (Date.now() - eventDate.getTime()) / (1000 * 60 * 60 * 24);
    if (daysSinceEvent >= 6) {
      return `Event occurred ${Math.floor(daysSinceEvent)} days ago. Recovery is only available within 6 days.`;
    }
    return '';
  });

  public ngOnInit(): void {
    const assignmentId = this.route.snapshot.queryParams.assignment_id;
    if (assignmentId && assignmentId !== this.assignmentIdToInspect()) {
      this.assignmentIdToInspect.set(assignmentId);
      this.loadAssignment(assignmentId);
    }
  }

  public ngOnDestroy() {
    this.reviewFormService.resetForm();
  }

  public onContextReady(ctx: IncidentFormContext) {
    this.incidentDate.set(ctx.instanceAtDate);
    this.form.set(ctx.form);
  }

  public forceValidateReview() {
    const id = this.assignmentIdToInspect();
    if (!id) return;
    this.lpService.submitReviewForceValidate(id).subscribe(
      () => this.notifyService.notify(`Successfully forced validated the review ${id}`),
      (err) => this.notifyService.notify(tryGetErrorMessage(err)),
    );
  }

  public recoverReview() {
    const id = this.assignmentIdToInspect();
    if (!id) return;
    this.isRecovering.set(true);
    this.lpService
      .recoverReview(id)
      .pipe(finalize(() => this.isRecovering.set(false)))
      .subscribe(
        () => {
          this.notifyService.notify(`Successfully recovered review ${id}`);
          this.loadAssignment(id);
        },
        (err) => this.notifyService.notify(tryGetErrorMessage(err)),
      );
  }

  public loadAssignment(assignmentId: string) {
    this.isLoading.set(true);
    this.loadErrorMessage.set(undefined);
    this.incidentFormRef.set(undefined);
    this.assignmentBeingInspected.set(undefined);
    this.assignmentType.set(undefined);
    this.assignmentStatus.set(undefined);
    this.reviewOutcome.set(undefined);
    this.form.set(undefined);
    this.submitErrorMessage.set(undefined);
    this.assignmentService
      .getAssignment(assignmentId, { skipAssignment: true })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(
        (assignment) => {
          // update URL query params for convenience
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { assignment_id: assignment.assignment_id },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
          const subjectType = parseReviewSubjectType(assignment.subject_type);
          if (subjectType !== ReviewSubjectType.Validation) {
            this.setAssignmentValues(assignment);
          } else {
            // When we load a validation, instead we want to load the original review, which has all the details
            this.assignmentService
              .getAssignment(assignment.subject_id, { skipAssignment: true })
              .pipe(finalize(() => this.isLoading.set(false)))
              .subscribe({
                next: (originalReview) => this.setAssignmentValues(originalReview),
                error: (err) => this.loadErrorMessage.set(tryGetErrorMessage(err)),
              });
          }
        },
        (err) => {
          if (hasStatus(err) && err.status === 404) this.loadErrorMessage.set('Assignment not found (or archived)');
          else this.loadErrorMessage.set(tryGetErrorMessage(err));
        },
      );
  }

  private setAssignmentValues(assignment: AssignmentDetails) {
    this.assignmentDetailsStore.set(assignment);
    this.assignmentBeingInspected.set(assignment);
    const subjectType = parseReviewSubjectType(assignment.subject_type);
    this.assignmentType.set(subjectType);
    this.assignmentStatus.set(parseReviewStatus(assignment.status));
    // admin has ability to immediately "clear" or "refresh" video clip passwords
    this.passwordAction.set(assignment.rhombus_password_opt_out ? 'clear' : 'refresh');
    // since assignment loaded successfully, lets try to load the existing submission (if completed)
    const investigationId = subjectType === ReviewSubjectType.Validation ? assignment.subject_id : assignment.assignment_id;
    this.assignmentService.getReviewOutcomes(investigationId).subscribe((outcomes) => {
      if ((outcomes?.length ?? 0) === 0) return;
      const last = outcomes[outcomes.length - 1];
      this.reviewOutcome.set(last);
      const v = reviewOutcomeToFormValues(last);
      this.reviewFormService.applyInitialValues(v);
    });
  }

  public submitReview() {
    const f = this.form();
    if (!f) return;
    const review = f.value;
    const findings = this.reviewFormService.findingsForm.value;

    this.isSubmitting.set(true);
    this.reviewFormService
      .uploadFiles()
      .pipe(finalize(() => this.isSubmitting.set(false)))
      .subscribe(
        ({ uploadedPhotosLinks, uploadedReportsLinks }) => {
          const submitRequest: SubmitReviewRequest = {
            is_theft: review.isTheft,
            case_id: review.matchingCase === 'new_case' ? null : review.matchingCase,
            report_links: uploadedReportsLinks,
            photos_links: uploadedPhotosLinks,
            extra_links: review.extraLinks.map((l) => ({ link: l })),
            theft_type: {
              is_cancellation: review.theftType.is_cancellation ?? false,
              is_underringing: review.theftType.is_underringing ?? false,
              is_walkout: review.theftType.is_walkout ?? false,
              is_cashier: review.theftType.is_cashier ?? false,
            },
            suspected_items: review.suspectedItems?.map((i) => ({ id: i.id!, total: i.total })),
            comments: review.isTheft ? (findings.comments ?? '') : this.reviewFormService.getNtfReason(),
            additional_stolen_value: review.additionalStolenValue,
            instance_at: this.reviewFormService.getValidInstanceAt(review.instance_at, this.incidentDate()),
            recent_walk_in_ids: review.selectedImages,
            use_kiosk_photo: review.useKioskPhoto,
            annotations: this.reviewOutcome()?.keystore_parsed.annotations,
          };
          const reviewId = this.assignmentBeingInspected().assignment_id;

          const submitOrEdit = review.reviewOutcomeId
            ? this.assignmentService.updateReviewOutcome(reviewId, review.reviewOutcomeId, submitRequest)
            : this.lpService.submitReview(reviewId, submitRequest);
          submitOrEdit.pipe(finalize(() => this.isSubmitting.set(false))).subscribe({
            next: () => this.submittedSwal()?.fire(),
            error: (err) => {
              const errorRequest = {
                comments: JSON.stringify(err),
                error_message: err.error?.error ?? '',
                errors: [{ is_blocking: false, review_error_type: 'Exception' }],
              };
              this.lpService.submitError(this.assignmentIdToInspect(), errorRequest).subscribe();
              this.submitErrorMessage.set(tryGetErrorMessage(err));
              this.failedModal()?.open();
            },
          });
        },
        (err) => this.notifyService.notify(`Failed to upload images: ${tryGetErrorMessage(err)}`),
      );
  }
}
